import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import type { BusinessArea } from "@/lib/types";

export default function BusinessPage() {
  const { t } = useTranslation();
  const businessAreas = t("business.areas", { returnObjects: true }) as unknown as BusinessArea[];

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title={t("business.page.title")}
        subtitle={t("business.page.subtitle")}
        breadcrumb={[{ label: t("business.page.title") }]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20industrial%20rubber%20manufacturing%20facility%20interior%20with%20organized%20compression%20molding%20presses%20and%20production%20line%2C%20clean%20factory%20floor%2C%20professional%20engineering%20environment%2C%20warm%20industrial%20lighting%2C%20high%20quality%20manufacturing%20photography&width=1600&height=680&seq=krubber-business-banner&orientation=landscape"
      />
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col gap-16 lg:gap-24">
            {businessAreas.map((area, index) => (
              <div
                key={area.id}
                className={`flex flex-col ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                } gap-10 lg:gap-16 items-center`}
              >
                <div className="lg:w-1/2 overflow-hidden rounded-2xl border border-background-200/70">
                  <img
                    src={area.image}
                    alt={area.title}
                    className="w-full h-[320px] md:h-[400px] object-cover"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-10 h-10 rounded-xl ${area.color} flex items-center justify-center`}>
                      <i className={`${area.icon} text-background-50 text-lg`} />
                    </span>
                    <span className="text-xs font-bold text-foreground-500 uppercase tracking-wider">
                      {area.subtitle}
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                    {area.title}
                  </h2>
                  <p className="mt-4 text-sm text-foreground-600 leading-relaxed">{area.desc}</p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {area.features.map((f) => (
                      <div key={f} className="flex items-start gap-2">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-primary-500 text-sm" />
                        </span>
                        <span className="text-sm text-foreground-700">{f}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {area.applications.map((app) => (
                      <span
                        key={app}
                        className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700"
                      >
                        {app}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t("business.inquiry")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}